"use client";

import Image from "next/image";
import { FaFacebook, FaInstagram, FaLinkedin, FaTiktok } from "@/components/icons";
import { SiThreads } from "@/components/icons";

import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { LocalizedLink as Link } from "@/components/i18n/LocalizedLink";
import { CookieSettingsButton } from "@/features/consent/components/CookieBanner";
import { hasArticles } from "@/features/knowledge-bank/articles";
import { useI18n } from "@/i18n/I18nProvider";

import { supportEmail as SUPPORT_EMAIL } from "@/data/faq";

/** `external` renderas som vanlig `<a>` — mailto ska inte gå genom next/link. */
type FooterLink = { href: string; label: string; external?: boolean };

const NAV_LINK_CLASS =
  "text-xs font-bold uppercase tracking-wide text-brand-beige-100 transition-colors hover:underline hover:underline-offset-4";

const SOCIAL_LINKS = [
  { href: "https://www.linkedin.com/company/campuslyan", label: "LinkedIn", icon: <FaLinkedin /> },
  { href: "https://www.instagram.com/campuslyan.se/", label: "Instagram", icon: <FaInstagram /> },
  { href: "https://www.facebook.com/campuslyan", label: "Facebook", icon: <FaFacebook /> },
  { href: "https://www.tiktok.com/@campuslyan", label: "TikTok", icon: <FaTiktok /> },
  { href: "https://www.threads.net/@campuslyan", label: "Threads", icon: <SiThreads /> },
];

export default function SiteFooter() {
  const year = new Date().getFullYear();
  const { t } = useI18n();
  const platformLinks: FooterLink[] = [
    { href: "/housing", label: t("siteFooter.links.housing") },
    { href: "/all-queues", label: t("siteFooter.links.allQueues") },
    { href: "/cities", label: t("siteFooter.links.cities") },
  ];
  const partnerLinks: FooterLink[] = [
    { href: "/partners#grundande-partners", label: t("siteFooter.links.foundingPartners") },
    { href: "/partners#bostadsforetag", label: t("siteFooter.links.housingCompanies") },
    { href: "/partners#ovriga-partners", label: t("siteFooter.links.otherPartners") },
  ];
  const companyLinks: FooterLink[] = [
    { href: "/about-us", label: t("siteFooter.links.about") },
    { href: "/for-business", label: t("siteFooter.links.forBusiness") },
    { href: "/faq", label: t("siteFooter.links.faq") },
    // Länkas först när det finns publicerade artiklar — en tom sida ska
    // inte få länkkraft.
    ...(hasArticles() ? [{ href: "/knowledge-bank", label: t("siteFooter.links.knowledgeBank") }] : []),
    { href: `mailto:${SUPPORT_EMAIL}`, label: t("siteFooter.links.contact"), external: true },
  ];
  // Policylänkarna hör ihop med copyrighten, inte med navigationen ovanför —
  // i kolumnen drog de blicken från Om oss och Vanliga frågor.
  const legalLinks: FooterLink[] = [
    { href: "/terms-of-service", label: t("siteFooter.links.terms") },
    { href: "/privacy-policy", label: t("siteFooter.links.privacy") },
    { href: "/cookie-policy", label: t("siteFooter.links.cookies") },
  ];

  return (
    <footer className="relative mt-40 w-full bg-brand sm:mt-48 lg:mt-56">
      <div className="absolute left-0 right-0 top-0 z-10 w-full -translate-y-[88%] overflow-hidden leading-[0]">
        <div className="relative h-[72px] w-full sm:h-[104px] lg:h-[136px]">
          <Image
            src="/footer-decoration.svg"
            alt=""
            fill
            className="object-fill"
            sizes="100vw"
          />
        </div>
      </div>

      <div className="relative z-20 mx-auto w-full max-w-[1400px] px-6 pb-16 pt-8 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="flex flex-col gap-8 lg:col-span-5">
            <div className="flex flex-row items-center gap-4 sm:gap-5">
              <Link href="/" className="relative block h-14 w-14 shrink-0" aria-label={t("siteFooter.homeAria")}>
                <Image
                  src="/campuslyan-logo.svg"
                  alt=""
                  fill
                  className="object-contain brightness-0 invert"
                />
              </Link>

              <div className="min-w-0 border-l-2 border-brand-green-light py-1 pl-4 sm:pl-5">
                <div className="max-w-md leading-tight text-brand-beige-100">
                  <span className="block text-2xl font-bold leading-none sm:text-[1.7rem]">
                    CampusLyan
                  </span>
                  <span className="mt-1.5 block text-sm font-light leading-relaxed sm:text-[0.95rem]">
                    {t("siteFooter.tagline")}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-4 text-lg">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="text-brand-beige-100 transition-colors hover:opacity-80"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-10 pt-4 lg:col-span-7 lg:pl-10">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-10">
              {[
                { title: t("siteFooter.sections.platform"), links: platformLinks },
                { title: t("siteFooter.sections.partners"), links: partnerLinks },
                { title: t("siteFooter.sections.campuslyan"), links: companyLinks },
              ].map((section) => (
                <div key={section.title}>
                  <div className="mb-5 flex items-center gap-2 text-[0.8rem] font-bold uppercase tracking-widest text-brand-beige-100 sm:text-sm">
                    <span className="h-4 w-[2px] bg-brand-green-light" />
                    {section.title}
                  </div>
                  <ul className="flex flex-col gap-3">
                    {section.links.map((link) => (
                      <li key={link.href}>
                        {link.external ? (
                          <a href={link.href} className={NAV_LINK_CLASS}>
                            {link.label}
                          </a>
                        ) : (
                          <Link href={link.href} className={NAV_LINK_CLASS}>
                            {link.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 flex flex-col gap-5 border-t border-brand-green-light pt-6 text-xs font-medium text-brand-beige-100 lg:flex-row lg:items-center lg:justify-between">
          {/* Copyrighten sist på mobil: policylänkarna är det man faktiskt
              letar efter här nere, och raden är lång. */}
          <div className="order-last lg:order-first">
            {t("siteFooter.copyright", { year })}
          </div>

          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
            <nav aria-label={t("siteFooter.legalAria")}>
              <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
                {legalLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="transition-colors hover:underline hover:underline-offset-4"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <CookieSettingsButton className="transition-colors hover:underline hover:underline-offset-4" />
                </li>
              </ul>
            </nav>

            <LanguageSwitcher compact inverted />
          </div>
        </div>
      </div>
    </footer>
  );
}
